import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  MenuItem,
  Select,
  ButtonBase,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Calendar, TrendingDown, TrendingUp } from "lucide-react";
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  getCreatorRevenueTrendAPI,
  getDashboardEarningsTrendAPI,
} from "../../../../api/dashbordApi";
import type {
  IDashboardEarningsOverview,
  IDashboardSummary,
} from "./achievements";

type Period = "week" | "month";

export type EarningsStatKey =
  | "total"
  | "tips"
  | "paidContent"
  | "subscriptions";

type RawChartRow = {
  date: string;
  total: number;
  tips: number;
  subscriptions: number;
  sales: number;
  customVideoRequests: number;
  postPurchases: number;
};

type ChartPoint = {
  label: string;
  value: number;
};

type MyEarningsSectionProps = {
  summary: IDashboardSummary | null;
  overview: IDashboardEarningsOverview | null;
  loading: boolean;
  overviewLoading: boolean;
};

const STAT_CONFIG: Record<
  EarningsStatKey,
  { chartTitle: string; tooltipLabel: string }
> = {
  total: {
    chartTitle: "Total Earnings Daily",
    tooltipLabel: "Earnings",
  },
  tips: {
    chartTitle: "Total Tips Daily",
    tooltipLabel: "Tips",
  },
  paidContent: {
    chartTitle: "Paid Content Daily",
    tooltipLabel: "Paid content",
  },
  subscriptions: {
    chartTitle: "Subscriptions Daily",
    tooltipLabel: "Subscriptions",
  },
};

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: "transparent",
    border: "none",
    borderRadius: 0,
    marginBottom: "30px",
    padding: 0,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "24px",
    gap: "16px",
    flexWrap: "wrap",
    backgroundColor: "transparent",
  },
  contentPanel: {
    backgroundColor: "#1F1F1FCC",
    borderRadius: "12px",
    padding: 0,
    overflow: "hidden",
  },
  title: {
    color: "#FFFFFF",
    fontSize: "24px",
    fontWeight: 700,
    lineHeight: 1.25,
    [theme.breakpoints.down("sm")]: {
      fontSize: "16px",
    },
  },
  periodSelect: {
    color: "#FFFFFF",
    fontSize: "16px",
    fontWeight: 400,
    minWidth: "140px",
    "& .MuiOutlinedInput-root": {
      backgroundColor: "transparent",
    },
    "& .MuiSelect-select": {
      color: "#FFFFFF",
      fontSize: "16px",
      fontWeight: 400,
      padding: "8px 32px 8px 36px",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "& .MuiSelect-icon": {
      color: "#FFFFFF",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "12px",
      "& .MuiSelect-select": {
        fontSize: "12px",
      },
    },
  },
  periodSelectIcon: {
    position: "absolute",
    left: 10,
    top: "50%",
    transform: "translateY(-50%)",
    pointerEvents: "none",
    display: "flex",
    alignItems: "center",
  },
  statsRow: {
    display: "flex",
    flexWrap: "nowrap",
    marginBottom: 0,
  },
  statItem: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center",
    padding: "8px",
    minHeight: "auto",
    cursor: "pointer",
    borderRadius: 0,
    transition: "background-color 0.2s ease",
    border: "none !important",
    textAlign: "left",
    font: "inherit",
    color: "inherit",
    outline: "none",
    boxSizing: "border-box",
    "&:focus": {
      outline: "none",
    },
    "&.Mui-focusVisible": {
      outline: "none",
    },
    [theme.breakpoints.down("sm")]: {
      flex: "1 1 0",
      minWidth: 0,
      padding: "8px",
    },
  },
  statItemInactive: {
    backgroundColor: "#161514 !important",
    border: "none !important",
    borderRight: "1px solid #1F1F1F !important",
    "&:last-child": {
      borderRight: "none !important",
    },
    "&:hover": {
      backgroundColor: "#161514 !important",
    },
  },
  statItemActive: {
    backgroundColor: "#1b1b1b !important",
    border: "none !important",
    borderRadius: 0,
    outline: "none",
    "&:hover": {
      backgroundColor: "#1b1b1b !important",
    },
    "&:focus": {
      outline: "none",
    },
    "&.Mui-focusVisible": {
      outline: "none",
    },
  },
  statLabel: {
    color: "#FFFFFF",
    fontSize: "16px",
    fontWeight: 500,
    marginBottom: "6px",
    lineHeight: 1.25,
    [theme.breakpoints.down("sm")]: {
      fontSize: "10px",
      fontWeight: 400,
    },
  },
  statValue: {
    color: "#FFFFFF",
    fontSize: "24px",
    fontWeight: 500,
    lineHeight: 1.2,
    marginBottom: "6px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "12px",
    },
  },
  statTrend: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "14px",
    fontWeight: 400,
    lineHeight: 1.25,
    [theme.breakpoints.down("sm")]: {
      fontSize: "10px",
    },
  },
  trendPositive: {
    color: "#73C75C",
  },
  trendNegative: {
    color: "#B86483",
  },
  chartSection: {
    padding: "24px 24px 28px",
    boxSizing: "border-box",
    border: "none",
    backgroundColor: "#1b1b1b",
    [theme.breakpoints.down("sm")]: {
      padding: "16px 16px 20px",
    },
  },
  chartTitle: {
    color: "#FFFFFF",
    fontSize: "16px",
    fontWeight: 600,
    marginBottom: "20px",
    lineHeight: 1.25,
  },
  chartWrap: {
    height: "300px",
    width: "100%",
    backgroundColor: "transparent",
    borderRadius: 0,
    padding: 0,
    boxSizing: "border-box",
    [theme.breakpoints.down("sm")]: {
      height: "240px",
    },
  },
  chartLoading: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    color: "#FFFFFF",
  },
  menuPaper: {
    backgroundColor: "#1a1a1a",
    border: "1px solid #363636",
  },
  menuItem: {
    color: "#FFFFFF",
    fontSize: "14px",
  },
}));

const formatMoney = (value: number) =>
  `$${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDateLabel = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });

const sortRowsByDate = (rows: RawChartRow[]) =>
  [...rows].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );

const filterRowsByPeriod = (
  rows: RawChartRow[],
  period: Period
): RawChartRow[] => {
  const sorted = sortRowsByDate(rows);

  if (period === "week") {
    return sorted.slice(-7);
  }

  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();

  return sorted.filter(row => {
    const d = new Date(row.date);
    return d.getFullYear() === year && d.getMonth() === month;
  });
};

const getRowValue = (row: RawChartRow, key: EarningsStatKey): number => {
  switch (key) {
    case "tips":
      return row.tips ?? 0;
    case "paidContent":
      return (
        (row.sales ?? 0) +
        (row.customVideoRequests ?? 0) +
        (row.postPurchases ?? 0)
      );
    case "subscriptions":
      return row.subscriptions ?? 0;
    case "total":
    default:
      return row.total ?? 0;
  }
};

const MyEarningsSection: React.FC<MyEarningsSectionProps> = ({
  summary,
  overview,
  loading,
  overviewLoading,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [period, setPeriod] = useState<Period>("week");
  const [selectedStat, setSelectedStat] = useState<EarningsStatKey>("total");
  const [rawChartRows, setRawChartRows] = useState<RawChartRow[]>([]);
  const [chartLoading, setChartLoading] = useState(true);

  const periodLabel = period === "week" ? "last week" : "last month";

  const paidContent =
    (overview?.sales ?? 0) + (overview?.customVideoRequests ?? 0);

  const stats = useMemo(
    () => [
      {
        key: "total" as const,
        label: "Total Earnings",
        value: summary?.totalEarningsThisMonth ?? 0,
        change: summary?.totalEarningsChange,
        format: "money" as const,
      },
      {
        key: "tips" as const,
        label: "Total Tips",
        value: summary?.totalTipsThisMonth ?? 0,
        change: summary?.totalTipsChange,
        format: "money" as const,
      },
      {
        key: "paidContent" as const,
        label: "Paid content",
        value: paidContent,
        change: undefined,
        format: "money" as const,
      },
      {
        key: "subscriptions" as const,
        label: "Subscriptions",
        value: overview?.subscriptions ?? 0,
        change: undefined,
        format: "money" as const,
      },
    ],
    [summary, overview, paidContent]
  );

  const chartData: ChartPoint[] = useMemo(
    () =>
      filterRowsByPeriod(rawChartRows, period).map(row => ({
        label: formatDateLabel(row.date),
        value: getRowValue(row, selectedStat),
      })),
    [rawChartRows, selectedStat, period]
  );

  const chartMeta = STAT_CONFIG[selectedStat];

  useEffect(() => {
    let cancelled = false;

    const loadChart = async () => {
      setChartLoading(true);
      try {
        if (period === "week") {
          const response = await (getCreatorRevenueTrendAPI as any)({
            granularity: "daily",
            preset: "week",
            fillGaps: true,
          });
          const rows: RawChartRow[] = (response?.data ?? []).map(
            (item: {
              date: string;
              total?: number;
              tips?: number;
              subscriptions?: number;
              sales?: number;
              customVideoRequests?: number;
              postPurchases?: number;
            }) => ({
              date: item.date,
              total: item.total ?? 0,
              tips: item.tips ?? 0,
              subscriptions: item.subscriptions ?? 0,
              sales: item.sales ?? 0,
              customVideoRequests: item.customVideoRequests ?? 0,
              postPurchases: item.postPurchases ?? 0,
            })
          );
          if (!cancelled) setRawChartRows(rows);
        } else {
          const currentDate = new Date();
          const month = `${currentDate.getFullYear()}-${String(
            currentDate.getMonth() + 1
          ).padStart(2, "0")}`;
          const year = currentDate.getFullYear().toString();

          try {
            const response = await (getCreatorRevenueTrendAPI as any)({
              granularity: "daily",
              preset: "month",
              fillGaps: true,
            });
            const rows: RawChartRow[] = (response?.data ?? []).map(
              (item: {
                date: string;
                total?: number;
                tips?: number;
                subscriptions?: number;
                sales?: number;
                customVideoRequests?: number;
                postPurchases?: number;
              }) => ({
                date: item.date,
                total: item.total ?? 0,
                tips: item.tips ?? 0,
                subscriptions: item.subscriptions ?? 0,
                sales: item.sales ?? 0,
                customVideoRequests: item.customVideoRequests ?? 0,
                postPurchases: item.postPurchases ?? 0,
              })
            );
            if (!cancelled && rows.length > 0) {
              setRawChartRows(rows);
              return;
            }
          } catch {
            /* fallback to earnings-daily */
          }

          const trendData = await getDashboardEarningsTrendAPI({ month, year });
          const rows: RawChartRow[] = (
            Array.isArray(trendData) ? trendData : []
          ).map(
            (
              item: {
                date?: string;
                day?: string;
                total?: number;
                tips?: number;
                subscriptions?: number;
                sales?: number;
                customVideoRequests?: number;
              },
              index: number
            ) => {
              const dateStr =
                item.date ??
                item.day ??
                new Date(
                  currentDate.getFullYear(),
                  currentDate.getMonth(),
                  index + 1
                ).toISOString();
              return {
                date: dateStr,
                total: item.total ?? 0,
                tips: item.tips ?? 0,
                subscriptions: item.subscriptions ?? 0,
                sales: item.sales ?? 0,
                customVideoRequests: item.customVideoRequests ?? 0,
                postPurchases: 0,
              };
            }
          );
          if (!cancelled) setRawChartRows(rows);
        }
      } catch {
        if (!cancelled) setRawChartRows([]);
      } finally {
        if (!cancelled) setChartLoading(false);
      }
    };

    loadChart();
    return () => {
      cancelled = true;
    };
  }, [period]);

  const renderTrend = (change?: number) => {
    if (change === undefined || change === null || Number.isNaN(change)) {
      return null;
    }
    const isPositive = change >= 0;
    const TrendIcon = isPositive ? TrendingUp : TrendingDown;
    const sign = isPositive ? "+" : "";
    return (
      <Typography
        component="span"
        className={`${classes.statTrend} ${
          isPositive ? classes.trendPositive : classes.trendNegative
        }`}
      >
        <TrendIcon size={14} />
        {sign}
        {Math.abs(change).toFixed(0)}% than {periodLabel}
      </Typography>
    );
  };

  const statsLoading = loading || overviewLoading;

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography className={classes.title}>My Earnings</Typography>
        <Box position="relative">
          <Box className={classes.periodSelectIcon}>
            <Calendar size={16} color="#FFFFFF" />
          </Box>
          <Select
            value={period}
            onChange={e => setPeriod(e.target.value as Period)}
            variant="outlined"
            className={classes.periodSelect}
            MenuProps={{
              classes: { paper: classes.menuPaper },
            }}
          >
            <MenuItem value="week" className={classes.menuItem}>
              This week
            </MenuItem>
            <MenuItem value="month" className={classes.menuItem}>
              This month
            </MenuItem>
          </Select>
        </Box>
      </Box>

      <Box className={classes.contentPanel}>
        <Box className={classes.statsRow}>
          {stats.map(stat => {
            const isActive = selectedStat === stat.key;
            return (
              <ButtonBase
                key={stat.key}
                disableRipple
                className={`${classes.statItem} ${
                  isActive ? classes.statItemActive : classes.statItemInactive
                }`}
                onClick={() => setSelectedStat(stat.key)}
                aria-pressed={isActive}
                aria-label={`Show ${stat.label} chart`}
              >
                <Typography className={classes.statLabel}>
                  {stat.label}
                </Typography>
                <Typography className={classes.statValue}>
                  {statsLoading
                    ? "..."
                    : stat.format === "money"
                      ? formatMoney(stat.value)
                      : stat.value}
                </Typography>
                {renderTrend(stat.change)}
              </ButtonBase>
            );
          })}
        </Box>

        <Box className={classes.chartSection}>
          <Typography className={classes.chartTitle}>
            {chartMeta.chartTitle}
          </Typography>
          <Box className={classes.chartWrap}>
            {chartLoading ? (
              <Box className={classes.chartLoading}>
                <CircularProgress size={28} style={{ color: "#FE5A97" }} />
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData}
                  barCategoryGap="18%"
                  margin={{
                    top: 12,
                    right: 12,
                    left: 0,
                    bottom: 4,
                  }}
                >
                  <XAxis
                    dataKey="label"
                    tick={{ fill: "#FFFFFF", fontSize: isMobile ? 11 : 12 }}
                    axisLine={false}
                    tickLine={false}
                    interval="preserveStartEnd"
                    dy={8}
                  />
                  <YAxis
                    tick={{ fill: "#FFFFFF", fontSize: isMobile ? 11 : 12 }}
                    axisLine={false}
                    tickLine={false}
                    width={isMobile ? 28 : 32}
                    tickCount={10}
                    allowDecimals={false}
                    orientation="left"
                    tickFormatter={v => {
                      const val = Number(v);
                      if (val >= 1000) return `$${(val / 1000).toFixed(0)}K`;
                      return `$${val}`;
                    }}
                  />
                  <Tooltip
                    cursor={{ fill: "rgba(255, 208, 225, 0.12)" }}
                    contentStyle={{
                      background: "#0b0b0f",
                      border: "1px solid #363636",
                      borderRadius: 8,
                      color: "#fff",
                    }}
                    formatter={(v: number) => [
                      formatMoney(v),
                      chartMeta.tooltipLabel,
                    ]}
                  />
                  <Bar
                    dataKey="value"
                    fill="#FFD0E1"
                    radius={[999, 999, 999, 999]}
                    maxBarSize={40}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MyEarningsSection;
